import { isHeartbeatReportReady, type AirshowHeartbeatRun } from "./airshowHeartbeatContract";

export type HeartbeatPollResult =
  | { kind: "not-modified"; etag: string | null }
  | { kind: "status"; status: AirshowHeartbeatRun; etag: string | null };

export interface HeartbeatPollInput {
  runId: string;
  etag: string | null;
  signal: AbortSignal;
}

interface PollerOptions {
  poll: (input: HeartbeatPollInput) => Promise<HeartbeatPollResult>;
  isVisible: () => boolean;
  onStatus?: (status: AirshowHeartbeatRun) => void;
  onTerminal?: (status: AirshowHeartbeatRun) => void;
  onError?: (error: unknown, consecutiveErrors: number) => void;
}

const ERROR_DELAYS = [2_000, 4_000, 8_000, 15_000] as const;

export class AirshowHeartbeatPoller {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private controller: AbortController | null = null;
  private runId: string | null = null;
  private etag: string | null = null;
  private errors = 0;
  private inFlight = false;
  private wakeAfterFlight = false;
  private generation = 0;

  constructor(private readonly options: PollerOptions) {}

  start(runId: string, etag: string | null = null): void {
    this.stop();
    this.runId = runId;
    this.etag = etag;
    this.generation += 1;
    this.schedule(this.options.isVisible() ? 2_000 : 10_000, this.generation);
  }

  stop(): void {
    this.generation += 1;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.controller?.abort();
    this.controller = null;
    this.runId = null;
    this.inFlight = false;
    this.wakeAfterFlight = false;
  }

  visibilityChanged(): void {
    if (!this.runId) return;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    if (this.inFlight) {
      if (this.options.isVisible()) this.wakeAfterFlight = true;
      return;
    }
    this.schedule(this.options.isVisible() ? 0 : 10_000, this.generation);
  }

  private schedule(delay: number, generation: number): void {
    if (!this.runId || generation !== this.generation) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.tick(generation);
    }, delay);
  }

  private async tick(generation: number): Promise<void> {
    const runId = this.runId;
    if (!runId || generation !== this.generation || this.inFlight) return;
    this.inFlight = true;
    const controller = new AbortController();
    this.controller = controller;
    let terminal = false;
    try {
      const result = await this.options.poll({ runId, etag: this.etag, signal: controller.signal });
      if (controller.signal.aborted || generation !== this.generation || runId !== this.runId) return;
      this.errors = 0;
      if (result.etag !== null) this.etag = result.etag;
      if (result.kind === "status") {
        this.options.onStatus?.(result.status);
        terminal = isHeartbeatReportReady(result.status);
        if (terminal) this.options.onTerminal?.(result.status);
      }
    } catch (error) {
      if (controller.signal.aborted || generation !== this.generation || runId !== this.runId) return;
      this.errors += 1;
      this.options.onError?.(error, this.errors);
    } finally {
      if (generation !== this.generation || runId !== this.runId) return;
      this.controller = null;
      this.inFlight = false;
      if (terminal) {
        this.stop();
      } else if (this.wakeAfterFlight && this.options.isVisible()) {
        this.wakeAfterFlight = false;
        this.schedule(0, generation);
      } else {
        const delay = this.errors > 0
          ? ERROR_DELAYS[Math.min(this.errors - 1, ERROR_DELAYS.length - 1)] ?? 15_000
          : this.options.isVisible() ? 2_000 : 10_000;
        this.schedule(delay, generation);
      }
    }
  }
}
